import * as THREE from "three";

/**
 * Soft contact shadows: one shared feathered circle texture + plane.
 * Cheap ground blobs under characters/props - no shadow maps.
 */

const sharedGeo = new THREE.PlaneGeometry(2, 2);
sharedGeo.rotateX(-Math.PI / 2);
sharedGeo.userData.shared = true;

let sharedMap: THREE.CanvasTexture | null = null;
const matCache = new Map<string, THREE.MeshBasicMaterial>();

function shadowMap(): THREE.CanvasTexture {
  if (sharedMap) {
    return sharedMap;
  }
  const size = 64;
  const c = document.createElement("canvas");
  c.width = size;
  c.height = size;
  const g = c.getContext("2d")!;
  const mid = size * 0.5;
  const grad = g.createRadialGradient(mid, mid, 0, mid, mid, mid * 0.98);
  // Dense circular contact blob - soft feather, no hard rim.
  grad.addColorStop(0, "rgba(18, 12, 6, 0.82)");
  grad.addColorStop(0.28, "rgba(18, 12, 6, 0.55)");
  grad.addColorStop(0.58, "rgba(18, 12, 6, 0.22)");
  grad.addColorStop(0.85, "rgba(18, 12, 6, 0.06)");
  grad.addColorStop(1, "rgba(18, 12, 6, 0)");
  g.fillStyle = grad;
  g.fillRect(0, 0, size, size);

  sharedMap = new THREE.CanvasTexture(c);
  sharedMap.colorSpace = THREE.SRGBColorSpace;
  sharedMap.magFilter = THREE.LinearFilter;
  sharedMap.minFilter = THREE.LinearFilter;
  sharedMap.generateMipmaps = false;
  sharedMap.needsUpdate = true;
  return sharedMap;
}

function shadowMat(opacity: number): THREE.MeshBasicMaterial {
  const key = opacity.toFixed(3);
  let m = matCache.get(key);
  if (!m) {
    m = new THREE.MeshBasicMaterial({
      map: shadowMap(),
      transparent: true,
      opacity,
      depthWrite: false,
      color: 0xffffff,
    });
    m.userData.shared = true;
    matCache.set(key, m);
  }
  return m;
}

export interface ContactShadowOpts {
  /** World radius along X (half-width of the decal). */
  radius?: number;
  /** Stretch along Z (1 = circle, <1 = oval). Default circle. */
  squash?: number;
  opacity?: number;
  y?: number;
}

/** Soft ground contact circle. Caller owns the mesh (don't dispose shared geo/mat). */
export function makeContactShadow(opts: ContactShadowOpts = {}): THREE.Mesh {
  const radius = opts.radius ?? 0.45;
  const squash = opts.squash ?? 1;
  const opacity = opts.opacity ?? 0.72;
  const mesh = new THREE.Mesh(sharedGeo, shadowMat(opacity));
  mesh.scale.set(radius, 1, radius * squash);
  mesh.position.y = opts.y ?? 0.02;
  mesh.renderOrder = -1;
  mesh.frustumCulled = true;
  return mesh;
}

/** Shared plane already rotated flat on XZ - for InstancedMesh batching. */
export function contactShadowGeometry(): THREE.BufferGeometry {
  return sharedGeo;
}

export function contactShadowMaterial(opacity: number): THREE.MeshBasicMaterial {
  return shadowMat(opacity);
}
